import { useEffect, useState } from 'react';
import type { PlandalfSdk } from './verified-gate';

type PlandalfStub = ((event: string, cb: (sdk: PlandalfSdk) => void) => void) &
  PlandalfSdk;

function readSdk(): PlandalfSdk | null {
  const candidate = window.plandalf as PlandalfStub | PlandalfSdk | undefined;
  if (!candidate) return null;
  if (
    typeof candidate.gate === 'function' ||
    typeof candidate.dispatch === 'function' ||
    typeof candidate.present === 'function'
  ) {
    return candidate;
  }
  return null;
}

export function usePlandalfSdk(): PlandalfSdk | null {
  const [sdk, setSdk] = useState<PlandalfSdk | null>(() => {
    const candidate = window.plandalf;
    return typeof candidate?.ready === 'function' ? null : readSdk();
  });

  useEffect(() => {
    if (sdk) return;

    let readyRequested = false;

    const assignReadySdk = (readySdk?: PlandalfSdk) => {
      const next = readySdk ?? readSdk();
      if (!next) return;

      const token = window.Bonjoro?.plandalf_token;
      if (token) next.identify?.(token);
      setSdk(next);
    };

    const waitForReady = () => {
      const browserSdk = window.plandalf as PlandalfStub | undefined;
      if (!browserSdk || readyRequested) return;

      if (typeof browserSdk.ready === 'function') {
        readyRequested = true;
        browserSdk.ready(assignReadySdk);
      } else if (typeof browserSdk === 'function') {
        readyRequested = true;
        browserSdk('ready', assignReadySdk);
      } else if (readSdk()) {
        assignReadySdk();
      }
    };

    waitForReady();
    if (readyRequested) return;

    const timer = window.setInterval(() => {
      waitForReady();
      if (readyRequested || readSdk()) {
        if (!readyRequested) assignReadySdk();
        window.clearInterval(timer);
      }
    }, 50);

    return () => window.clearInterval(timer);
  }, [sdk]);

  return sdk;
}
